import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { Button, Modal } from "src/components";
import usePermission from "src/hooks/usePermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import StockistSaoAckDialog, { STOCKIST_SAO_REJECT_REASONS } from "./StockistSaoAckDialog";

const STATUS_PILL: Record<string, { label: string; className: string }> = {
  PENDING: { label: "Pending", className: "bg-[#FFF9ED] text-[#996B0F]" },
  ACCEPTED: { label: "Confirmed", className: "bg-[#D3F6E4] text-[#207A65]" },
  REJECTED: { label: "Rejected", className: "bg-[#FBEEEE] text-[#A92626]" },
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const StockistSaoConfirmation = () => {
  const { orderDetailsFiltered } = useSelector((state: RootState) => state.ord);
  const orderDetails = orderDetailsFiltered?.[0];
  const [dialogMode, setDialogMode] = useState<"accept" | "reject" | null>(null);
  const { hasPermission } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.STOCKIST_SAO_ACK.module,
    action: fineGrainedPermissionsKeys.ORDER.STOCKIST_SAO_ACK.action,
  });

  const ackStatus = orderDetails?.stockist_sao_ack_status;
  if (orderDetails?.cndn_remarks_code_int !== 2 || !ackStatus || ackStatus === "NOT_APPLICABLE") return null;

  const pill = STATUS_PILL[ackStatus];
  const orderStatusId = orderDetails?.orderStatusHdr?.order_status_id;
  const isPending = ackStatus === "PENDING";
  const canAccept = isPending && hasPermission && [0, 2, 3].includes(orderStatusId);
  const canReject = isPending && hasPermission && orderStatusId === 0;
  const reasonLabel = STOCKIST_SAO_REJECT_REASONS.find((r) => r.key === orderDetails?.stockist_sao_reject_reason)?.name;

  return (
    <div>
      <p className="text-[#49484C] font-semibold mt-8 text-lg relative pr-4 after:content-[''] after:absolute after:w-[calc(100%-14rem)] after:h-[1px] after:bg-gray-300 after:top-1/2 after:right-0">
        Stockist SAO Confirmation
      </p>
      <div className="flex flex-col gap-2 mt-2">
        <p className="text-gray-500 flex items-center gap-2">
          Status:
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pill?.className ?? "bg-[#f1f1f1] text-black"}`}>
            {pill?.label ?? ackStatus}
          </span>
        </p>
        {!isPending && (
          <p className="text-gray-500">
            Date: <strong>{formatDate(orderDetails?.stockist_sao_ack_date)}</strong>
          </p>
        )}
        {ackStatus === "REJECTED" && (
          <p className="text-gray-500">
            Reason: <strong>{reasonLabel || orderDetails?.stockist_sao_reject_reason || "-"}</strong>
          </p>
        )}
        {!isPending && (
          <p className="text-gray-500">
            Remarks: <strong>{orderDetails?.stockist_sao_ack_remarks || "-"}</strong>
          </p>
        )}
        {(canAccept || canReject) && (
          <div className="flex gap-3 mt-2">
            {canAccept && (
              <Button className="text-white bg-[#207A65] px-4 py-2 rounded-lg" onClick={() => setDialogMode("accept")}>
                Accept
              </Button>
            )}
            {canReject && (
              <Button className="text-white bg-[#A92626] px-4 py-2 rounded-lg" onClick={() => setDialogMode("reject")}>
                Reject
              </Button>
            )}
          </div>
        )}
      </div>
      <Modal isOpen={dialogMode !== null} isHeader={false} onCloseModal={() => setDialogMode(null)}>
        {dialogMode && <StockistSaoAckDialog mode={dialogMode} onCloseModal={() => setDialogMode(null)} />}
      </Modal>
    </div>
  );
};

export default StockistSaoConfirmation;
